<template>
  <div>
    <slot />
    <dn-modal
      v-if="showShareModal"
      @close="showShareModal = false"
    >
      <div class="top-part">
        <h3>{{ t('Share the plus article with a friend without a subscription') }}</h3>
        <p>{{ t('The recipient must create a free user to read. Generating a link counts as one share.') }}</p>
      </div>
      <div class="interactive-part">
        <div class="content">
          <label>{{ shareCount.remaining }}</label>
          <p>
            {{ t('{remaining} out of {total} left in {month}', { remaining: shareCount.remaining, total: shareCount.total ,month: dayjs().format('MMMM') }) }}
          </p>
        </div>

        <template v-if="hasShareAbleUrl">
          <div class="input-group">
            <input
              class="input-field"
              type="text"
              :value="previewUrl"
              disabled
            >
            <dn-button
              icon="content_copy"
              :text="t('Copy link')"
              class="button"
              @on-click="onCopyClick"
            />
          </div>
          <p><span>{{ t('Note') }}:</span> {{ t('The link is unique and works for one user.') }}</p>
        </template>

        <dn-button
          v-if="showUrlGenerateButton"
          :text="t('Generate Link')"
          class="button gi-bort-button"
          @on-click="getShareAbleUrl"
        />
      </div>
    </dn-modal>
  </div>
</template>

<script setup lang="ts">

import dayjs from 'dayjs';
const { t } = useI18n();
const { setShareArticleInfo, shareCount, getShareAbleUrl, hasShareAbleUrl, shareArticle, previewUrl, showShareModal, showUrlGenerateButton, closeShareModal } = useArticleGiveAway();
const onCopyClick = () => {
  shareArticle();
  closeShareModal();
};

onMounted(() => {
  setShareArticleInfo();
});
</script>

<style lang="scss" scoped>
.top-part {
  width: calc(100% + var(--row-gap) * 2);
  height: 205px;

  /* eslint-disable-next-line max-len */
  background: var(--color-brand-utility-100) url('data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%2096%20119%22%20%20width%3D%2296%22%20height%3D%22119%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%20%20%20%20%3Cpath%20d%3D%22M91.06%2029.718H62.891c5.082-1.834%209.785-3.916%2011.78-5.948%205.177-5.402%205.177-14.125%200-19.527C72.059%201.517%2068.687.179%2065.314.179a12.816%2012.816%200%2000-9.357%204.064c-2.755%202.874-5.605%2011.3-7.648%2018.635-2.042-7.335-4.892-15.76-7.647-18.635C38.097%201.517%2034.724.179%2031.304.179c-3.42%200-6.745%201.338-9.357%204.064-5.178%205.402-5.178%2014.125%200%2019.527%201.947%202.032%206.697%204.163%2011.78%205.948H5.559c-2.612%200-4.75%202.23-4.75%204.956v22.798c0%202.726%202.138%204.956%204.75%204.956v51.545c0%202.725%202.138%204.956%204.75%204.956h76c2.613%200%204.75-2.231%204.75-4.956V62.428c2.613%200%204.75-2.23%204.75-4.956V34.674c0-2.726-2.137-4.956-4.75-4.956zm-80.75%2084.255V62.428h26.267v51.545H10.309zm-4.75-79.3h30.969v22.8H5.56v-22.8zM31.303%205.136c2.28%200%204.418.942%206.033%202.577%201.995%202.082%205.035%2011.052%207.457%2020.32-8.882-2.527-17.48-5.7-19.475-7.78-3.325-3.47-3.325-9.07%200-12.54%201.615-1.635%203.753-2.577%205.985-2.577zM71.3%207.712c3.325%203.47%203.325%209.07%200%2012.54-1.995%202.08-10.592%205.253-19.475%207.78%202.423-9.268%205.463-18.238%207.458-20.32%201.615-1.635%203.752-2.577%206.032-2.577%202.233%200%204.37.942%205.985%202.577zM46.124%2034.674h9.215v22.798H41.28V34.674H46.124zm-4.845%2079.299V62.527h14.013v51.446H41.279zm45.03-56.5H60.09v-22.8h30.97v22.8h-4.75zm0%2056.5H60.09V62.527v-.099h26.22v51.545z%22%20fill%3D%22%23006295%22%20fill-opacity%3D%22.08%22%2F%3E%0A%3C%2Fsvg%3E%0A') no-repeat 50%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  padding: var(--row-gap);
  margin: calc(-1 * var(--row-gap)) calc(-1 * var(--row-gap)) 0;

  h3, p {
    color: var(--color-brand-primary);
    line-height: var(--typography-editorial-subhead-min-line-height);
    font-weight: 500;
    margin-bottom: var(--row-gap);
  }

  p {
    font-size: var(--typography-utility-l-font-size);
    margin-bottom: 0;
  }
}

.interactive-part {
  .content {
    display: flex;
    gap: var(--row-gap);
    text-align: left;
    padding: var(--row-gap) 0;

    label {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 60px;
      height: 60px;
      border-radius: var(--box-radius);
      background: var(--color-brand-utility-100);
      font-family: var(--typography-editorial-subhead-max-font-family);
      font-size: var(--typography-editorial-subhead-max-font-size);
      color: var(--color-brand-primary);
    }

    p {
      font-size: var(--typography-utility-l-font-size);
      color: var(--color-brand-primary);
      line-height: var(--typography-editorial-subhead-min-line-height);
    }
  }

  .gi-bort-button {
    width: 100%;
    justify-content: center;
    padding: 12px 14px;
  }

  .input-group {
    display: flex;
    margin-bottom: 8px;

    .input-field {
      width: 100%;
      border: 1px solid var(--color-brand-utility-100);
      box-shadow: none;
      border-radius: var(--box-radius);
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
      font-size: var(--typography-utility-m-font-size);
      padding: 8px 10px;
    }

    .button {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }

    + p {
      font-size: var(--typography-utility-m-font-size);
      color: var(--color-brand-primary);
      line-height: var(--typography-editorial-subhead-min-line-height);
      text-align: left;

      span {
        font-family: var(--typography-editorial-subhead-max-font-family);
        font-weight: 500;
      }
    }
  }
}
</style>
